import React, { useEffect, useState } from "react";

import {
  formatFractionDisplay,
  formatFractionInput,
  getMidpointGrowingDate,
  queryIsLoading,
  scrollPage,
  useCropMethodology,
  useGetFarmFieldListAtPointInTime,
  useGetFarms,
  useGetFields,
  useGetFootprintingCropFootprint,
  useGetPracticeCrops,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticePlantProtections,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useNavigate,
  useTranslation,
} from "utils";

import { PRACTICE_SLUG } from "models";

import { Card, ListCard } from "components";

import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import { CropFertilizersForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFertilizersForm";
import { CropFieldSelectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFieldSelectionForm";
import { CropGeneralForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropGeneralForm";
import { CropIrrigationForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropIrrigationForm";
import { CropPlantProtectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropPlantProtectionForm";
import { CropSeedingForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropSeedingForm";
import { CropTillageForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTillageForm";
import { CropTransportForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTransportForm";
import { CropCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface FootprintingCropReviewProps {
  cropId: string;
  year?: number;
}

export const FootprintingCropReview = ({ cropId, year }: FootprintingCropReviewProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["footprintingCrop", "practices"]);
  const { currentFarm, isLoading: isGetFarmsLoading } = useGetFarms();
  const { isCfp } = useCropMethodology(currentFarm?.id ?? "");
  const [loading, setLoading] = useState<boolean>();

  const cropFootprintQuery = useGetFootprintingCropFootprint(currentFarm?.id, cropId, year);
  const tillagePracticesQuery = useGetPracticeTillages({ enabled: true });
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const mineralFertilizerPracticesQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerPracticesQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const plantProtectionPracticesQuery = useGetPracticePlantProtections();

  const allFieldsQuery = useGetFields({ farmId: currentFarm?.id });
  const regionId = allFieldsQuery.data?.[0]?.regionId;
  const allCropsQuery = useGetPracticeCrops({ regionId, enabled: !!regionId && !!allFieldsQuery.data });
  const midpointDateForYear = getMidpointGrowingDate(allCropsQuery.data?.find((c) => c.id === cropId), year);

  const fieldListAtPointInTimeQuery = useGetFarmFieldListAtPointInTime({
    farmId: currentFarm?.id,
    date: midpointDateForYear?.toISOString() ?? "",
    enabled: !!midpointDateForYear,
  });

  const naviagteToFormPage = (pageKey: string) => {
    navigate("/footprinting/form", { state: { year, formType: "farm", pageKey } });
  };

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`footprinting:radioAnswer.${isActive}`));

  useEffect(() => {
    scrollPage();
  }, [cropId]);

  useEffect(() => {
    setLoading(
      isGetFarmsLoading ||
        [
          cropFootprintQuery,
          tillagePracticesQuery,
          seedingPracticesQuery,
          mineralFertilizerPracticesQuery,
          organicFertilizerPracticesQuery,
          allFieldsQuery,
          allCropsQuery,
          fieldListAtPointInTimeQuery,
        ].some(queryIsLoading)
    );
  }, [
    isGetFarmsLoading,
    cropFootprintQuery,
    tillagePracticesQuery,
    seedingPracticesQuery,
    mineralFertilizerPracticesQuery,
    organicFertilizerPracticesQuery,
    allFieldsQuery,
    allCropsQuery,
    fieldListAtPointInTimeQuery,
  ]);

  return (
    <>
      <Card className="padding-125">
        <ReviewPageTitle
          title={t("footprinting:review.crop.title", { cropName: allCropsQuery.data?.find((c) => c.id === cropId)?.name })}
          iconName="klim/crops-matured"
          isLoading={loading}
        />
        {/* Crop general form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.crop.general")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropGeneralForm.Key) }}
          dataPairs={[
            {
              label: t("cropGeneralForm.question.totalWeight"),
              value: `${cropFootprintQuery.data?.footprint?.totalWeightPerHa} ${cropFootprintQuery.data?.footprint?.totalWeightPerHaUnit}`,
            },
            {
              label: t("cropGeneralForm.question.preDryingMoistureContentPct"),
              value: `${formatFractionInput(cropFootprintQuery.data?.footprint?.preDryingMoistureContentPct ?? 0)} %`,
            },
            { label: t("cropGeneralForm.question.byproductPct"), value: `${formatFractionInput(cropFootprintQuery.data?.footprint?.byproductPct ?? 0)} %` },
            { label: t("cropGeneralForm.question.seedWeightKgPerHa"), value: `${cropFootprintQuery.data?.footprint?.seedWeightKgPerHa} kg/ha` },
            { label: t("cropGeneralForm.question.soilPh"), value: `${cropFootprintQuery.data?.footprint?.soilPh} pH` },
          ]}
        />
        {/* Crop fields form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.crop.fields")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropFieldSelectionForm.Key) }}
          dataPairs={cropFootprintQuery.data?.footprint?.fieldIds.map((fieldId) => {
            const field = fieldListAtPointInTimeQuery.data?.find((f) => f.id === fieldId);

            return { label: field?.name ?? "", value: `${field?.size ?? 0} ha` };
          })}
        />
        {/* Crop tillage form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.crop.tillage")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropTillageForm.Key) }}
          dataPairs={[
            {
              label: t("cropTillageForm.tillage.question"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.tillageActive ?? null),
              children: cropFootprintQuery.data?.footprint?.tillageActive
                ? cropFootprintQuery.data?.footprint?.tillageUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropTillageForm.tillage.count") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("cropTillageForm.tillage.type"),
                          value: t(`${PRACTICE_SLUG.TILLAGES}.${tillagePracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`, {
                            ns: "practices",
                          }),
                        },
                        {
                          label: t("cropTillageForm.tillage.depth"),
                          value: `${i.tillageDepth} cm`,
                        },
                        {
                          label: t("cropTillageForm.question.implementedOn"),
                          value: `${i.implementedOnHa} ha`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
        {/* Crop seeding form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.crop.seeding")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropSeedingForm.Key) }}
          dataPairs={[
            {
              label: t("cropSeedingForm.seeding.question"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.seedingActive ?? null),
              children: cropFootprintQuery.data?.footprint?.seedingActive
                ? cropFootprintQuery.data?.footprint?.seedingUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropSeedingForm.seeding.count") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("cropSeedingForm.seeding.type"),
                          value: t(`${PRACTICE_SLUG.SEEDINGS}.${seedingPracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`, {
                            ns: "practices",
                          }),
                        },
                        {
                          label: t("cropSeedingForm.question.implementedOn"),
                          value: `${i.implementedOnHa} ha`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
        {/* Crop fertilizers form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.crop.fertilizers")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropFertilizersForm.Key) }}
          dataPairs={[
            {
              label: t("cropFertilizerForm.synthetic.question"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.mineralFertilizerActive ?? null),
              children: cropFootprintQuery.data?.footprint?.mineralFertilizerActive
                ? cropFootprintQuery.data?.footprint?.mineralFertilizerUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropFertilizerForm.application.title") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("cropFertilizerForm.application.productName"),
                          value: t(
                            `${PRACTICE_SLUG.MINERAL_FERTILIZERS}.${mineralFertilizerPracticesQuery.data?.find((p) => p.id === i.applicationId)
                              ?.translationSlug}`,
                            { ns: "practices" }
                          ),
                        },
                        {
                          label: t("cropFertilizerForm.application.amount"),
                          value: `${i.amount} ${i.amountUnit}`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
            {
              label: t("cropFertilizerForm.organic.question"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.organicFertilizerActive ?? null),
              children: cropFootprintQuery.data?.footprint?.organicFertilizerActive
                ? cropFootprintQuery.data?.footprint?.organicFertilizerUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropFertilizerForm.application.title") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("cropFertilizerForm.application.productName"),
                          value: t(
                            `${PRACTICE_SLUG.ORGANIC_FERTILIZERS}.${organicFertilizerPracticesQuery.data?.find((p) => p.id === i.applicationId)
                              ?.translationSlug}`,
                            { ns: "practices" }
                          ),
                        },
                        {
                          label: t("cropFertilizerForm.application.nitrogen"),
                          value: `${i.nValue} %`,
                        },
                        {
                          label: t("cropFertilizerForm.application.amount"),
                          value: `${i.amount} ${i.amountUnit}`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
            { label: t("cropFertilizerForm.application.lime"), value: `${cropFootprintQuery.data?.footprint?.limeT} tonnes` },
          ]}
        />
        {/* Crop plant protection form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.crop.plantProtection")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropPlantProtectionForm.Key) }}
          dataPairs={[
            {
              label: t("cropPlantProtectionForm.question.plantProtectionQuestionLabel"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.plantProtectionActive ?? null),
              children: cropFootprintQuery.data?.footprint?.plantProtectionActive
                ? cropFootprintQuery.data?.footprint?.plantProtectionUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropPlantProtectionForm.question.applicationNumber", { count: index + 1 })}
                      dataPairs={[
                        {
                          label: t("cropPlantProtectionForm.question.plantProtectionTypeQuestionLabel"),
                          value: t(
                            `${PRACTICE_SLUG.PLANT_PROTECTIONS}.${plantProtectionPracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`,
                            { ns: "practices" }
                          ),
                        },
                        {
                          label: t("cropPlantProtectionForm.question.amountAppliedQuestionLabel"),
                          value: `${i.amount} ${i.amountUnit}`,
                        },
                        {
                          label: t("cropPlantProtectionForm.question.activeIngredientsQuestionLabel"),
                          value: `${formatFractionDisplay(i.activeIngredientsPct ?? 0)} %`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
        {/* Crop irrigation form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.crop.irrigation")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropIrrigationForm.Key) }}
          dataPairs={[
            {
              label: t("cropIrrigation.question.irrigation"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.irrigationActive ?? null),
            },
            ...(cropFootprintQuery.data?.footprint?.irrigationActive
              ? [
                  {
                    label: t("cropIrrigation.question.waterUsedQuestionLabel"),
                    value: `${cropFootprintQuery.data?.footprint?.waterUsedM3} m³`,
                  },
                ]
              : []),
            ...(isCfp && cropFootprintQuery.data?.footprint?.irrigationActive
              ? [
                  {
                    label: t("cropIrrigation.question.irrigationApplicationMethod"),
                    value: cropFootprintQuery.data?.footprint?.irrigationApplicationMethod
                      ? t(`cropIrrigation.applicationMethod.${cropFootprintQuery.data.footprint.irrigationApplicationMethod}`)
                      : "",
                  },
                  {
                    label: t("cropIrrigation.question.irrigationWaterSource"),
                    value: cropFootprintQuery.data?.footprint?.irrigationWaterSource
                      ? t(`cropIrrigation.waterSource.${cropFootprintQuery.data.footprint.irrigationWaterSource}`)
                      : "",
                  },
                  {
                    label: t("cropIrrigation.question.irrigationPumpPowerSource"),
                    value: cropFootprintQuery.data?.footprint?.irrigationPumpPowerSource
                      ? t(`cropIrrigation.pumpPowerSource.${cropFootprintQuery.data.footprint.irrigationPumpPowerSource}`)
                      : "",
                  },
                  {
                    label: t("cropIrrigation.question.irrigationAreaPct"),
                    value: cropFootprintQuery.data?.footprint?.irrigationAreaPct != null ? `${cropFootprintQuery.data.footprint.irrigationAreaPct}%` : "",
                  },
                ]
              : []),
          ]}
        />
        {/* Crop transport form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:navigation.crop.transport")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropTransportForm.Key) }}
          dataPairs={[
            {
              label: t("cropTransportForm.question.radioQuestion"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.cropSaleActive ?? null),
              children: cropFootprintQuery.data?.footprint?.cropSaleActive
                ? cropFootprintQuery.data?.footprint?.cropSales.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("footprintingFarm:farmTransportForm.retailer", { count: index + 1 })}
                      dataPairs={[
                        {
                          label: t("cropTransportForm.question.purchaserNameQuestionLabel"),
                          value: i.purchaserName ?? "",
                        },
                        {
                          label: t("cropTransportForm.question.countryQuestionLabel"),
                          value: i.purchaserCountryCode ?? "",
                        },
                        {
                          label: t("cropTransportForm.question.postcodeQuestionLabel"),
                          value: i.purchaserPostCode ?? "",
                        },
                        {
                          label: t("cropTransportForm.question.shareQuestionLabel"),
                          value: `${i.soldYieldTs} t`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
        {/* Comment */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("footprinting:commentForm.title")}
          titleButton={{ label: t("footprinting:review.edit"), onClick: () => naviagteToFormPage(CropCommentForm.Key) }}
          dataPairs={[
            {
              label: t("footprinting:commentForm.label"),
              value: cropFootprintQuery.data?.footprint?.comment || t("footprinting:commentForm.noComment"),
            },
          ]}
        />
      </Card>
    </>
  );
};
